import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, MessageCircle, Share2 } from 'lucide-react';
import { brand } from '@shared/brand.js';
import { HOUSEHOLD_PROFILES, suggestedRoutines } from '@shared/routine-templates.js';
import { api, hpath, newIdempotencyKey } from '../../lib/api.js';
import { useSession } from '../../app/session.jsx';
import { useDocumentTitle, useForm } from '../../app/hooks.js';
import { useToast } from '../../components/Toast.jsx';
import { Button, Input, Select } from '../../components/ui.jsx';

const COMMON_TIMEZONES = [
  ['America/Sao_Paulo', 'Brasília (São Paulo, Rio, Sul, Nordeste)'],
  ['America/Manaus', 'Amazonas (Manaus)'],
  ['America/Cuiaba', 'Mato Grosso (Cuiabá)'],
  ['America/Campo_Grande', 'Mato Grosso do Sul'],
  ['America/Porto_Velho', 'Rondônia'],
  ['America/Boa_Vista', 'Roraima'],
  ['America/Rio_Branco', 'Acre'],
  ['America/Noronha', 'Fernando de Noronha'],
  ['Europe/Lisbon', 'Portugal (Lisboa)'],
];

const WEEKDAY_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

function routineWhen(r) {
  if (r.freq === 'daily') return 'todo dia';
  if (r.freq === 'monthly')
    return r.interval > 1 ? `a cada ${r.interval} meses` : `todo mês, dia ${r.monthDay}`;
  const days = r.weekdays.map((d) => WEEKDAY_SHORT[d]).join(' e ');
  return r.interval > 1 ? `a cada ${r.interval} semanas (${days})` : `toda semana (${days})`;
}

export function OnboardingPage() {
  useDocumentTitle('Boas-vindas');
  const { user, households, refresh } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzOptions = useMemo(() => {
    const list = COMMON_TIMEZONES.map(([value, label]) => ({ value, label }));
    if (detectedTz && !list.some((o) => o.value === detectedTz))
      list.unshift({ value: detectedTz, label: `${detectedTz} (deste aparelho)` });
    return list;
  }, [detectedTz]);

  const [step, setStep] = useState(1);
  const [household, setHousehold] = useState(null);
  const f = useForm({
    name: '',
    timezone: tzOptions.some((o) => o.value === detectedTz) ? detectedTz : 'America/Sao_Paulo',
    profile: '',
  });
  const [busy, setBusy] = useState(false);
  const [createKey] = useState(newIdempotencyKey);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [selected, setSelected] = useState(null);

  const routines = useMemo(() => suggestedRoutines(f.values.profile || 'solo'), [f.values.profile]);
  const chosen = selected ?? new Set(routines.filter((r) => r.recommended).map((r) => r.key));

  const createHousehold = async (e) => {
    e.preventDefault();
    if (!f.values.profile) {
      f.setErrors({ profile: 'Escolha a opção que mais combina com a casa.' });
      return;
    }
    setBusy(true);
    try {
      const h = await api('/api/households', {
        method: 'POST',
        body: { name: f.values.name, timezone: f.values.timezone, profile: f.values.profile },
        idempotencyKey: createKey,
      });
      setHousehold(h);
      await refresh();
      setStep(f.values.profile === 'solo' ? 3 : 2);
    } catch (err) {
      f.setErrors(err.fieldErrors ?? {});
      if (err.code !== 'validation') toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const createInvite = async () => {
    setBusy(true);
    try {
      const r = await api(hpath(household.id, '/invites'), {
        method: 'POST',
        body: { role: 'member', maxUses: 5, expiresInDays: 7 },
      });
      setInviteUrl(r.url);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inviteText = `${user?.name} convidou você para organizar a casa "${household?.name}" no ${brand.name}: ${inviteUrl}`;
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: brand.name, text: inviteText });
      } catch {
        /* cancelado pelo usuário */
      }
    } else {
      await navigator.clipboard?.writeText(inviteUrl);
      toast.success('Link copiado.');
    }
  };

  const finish = async (withRoutines) => {
    setBusy(true);
    try {
      if (withRoutines && chosen.size) {
        await api(hpath(household.id, '/tasks/routines'), {
          method: 'POST',
          body: { routines: [...chosen].map((key) => ({ key })) },
          idempotencyKey: `routines-${household.id}`,
        });
      }
      await api(hpath(household.id), { method: 'PATCH', body: { settings: { onboardingDone: true } } });
      await qc.invalidateQueries();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  return (
    <main className="auth-screen">
      <div className="auth-card" style={{ width: 'min(620px, 100%)' }}>
        <div className="auth-brand">
          <img src="/favicon.svg" alt="" />
          <h1>
            {step === 1
              ? households.length && params.get('novo')
                ? 'Criar outro lar'
                : `Olá, ${user?.name?.split(' ')[0]}!`
              : household?.name}
          </h1>
          <p>
            {step === 1 && 'Vamos preparar o seu lar em três passos rápidos. Dá para mudar tudo depois.'}
            {step === 1 && (
              <>
                {' '}
                <Link to="/condominio/entrar">Recebeu um código do seu condomínio? Entre por aqui.</Link>{' '}
                <Link to="/prestador">Quer oferecer serviços de reparo? Cadastre-se como prestador.</Link>
              </>
            )}
            {step === 2 && 'Chame quem mora com você. Cada pessoa vê e atualiza as mesmas listas e tarefas.'}
            {step === 3 &&
              'Escolha rotinas para começar. Ninguém fica responsável automaticamente: vocês distribuem depois.'}
          </p>
        </div>
        <div className="steps" aria-label={`Passo ${step} de 3`}>
          {[1, 2, 3].map((s) => (
            <span key={s} className={s <= step ? 'on' : ''} />
          ))}
        </div>

        <div className="card auth-panel">
          {step === 1 && (
            <form className="form" onSubmit={createHousehold} noValidate>
              <Input
                label="Nome do lar"
                placeholder="Ex.: Casa da Rua das Flores"
                required
                maxLength={80}
                autoFocus
                error={f.errors.name}
                {...f.bind('name')}
              />
              <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="field-label" style={{ marginBottom: 6 }}>
                  Quem mora aí?
                </legend>
                <div className="option-grid" role="radiogroup">
                  {HOUSEHOLD_PROFILES.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      role="radio"
                      aria-checked={f.values.profile === p.key}
                      className="option-card"
                      onClick={() => {
                        f.set('profile', p.key);
                        setSelected(null);
                      }}
                    >
                      <strong>{p.label}</strong>
                      <span>{p.description}</span>
                    </button>
                  ))}
                </div>
                {f.errors.profile && <span className="field-error">{f.errors.profile}</span>}
              </fieldset>
              <Select
                label="Fuso horário"
                options={tzOptions}
                hint="Usado para prazos, lembretes e o que é “hoje” no lar."
                error={f.errors.timezone}
                {...f.bind('timezone')}
              />
              <Button type="submit" variant="primary" block loading={busy}>
                Continuar
              </Button>
            </form>
          )}

          {step === 2 && (
            <div className="form">
              {!inviteUrl ? (
                <Button variant="primary" block loading={busy} onClick={createInvite}>
                  Gerar link de convite
                </Button>
              ) : (
                <>
                  <Input
                    label="Link de convite (vale 7 dias, até 5 pessoas)"
                    readOnly
                    value={inviteUrl}
                    onFocus={(e) => e.target.select()}
                  />
                  <div className="cluster">
                    <Button icon={Share2} variant="primary" onClick={share}>
                      Compartilhar
                    </Button>
                    <a
                      className="btn"
                      href={`https://wa.me/?text=${encodeURIComponent(inviteText)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <MessageCircle aria-hidden="true" /> WhatsApp
                    </a>
                    <Button
                      icon={Copy}
                      onClick={async () => {
                        await navigator.clipboard?.writeText(inviteUrl);
                        toast.success('Link copiado.');
                      }}
                    >
                      Copiar
                    </Button>
                  </div>
                </>
              )}
              <Button variant={inviteUrl ? 'primary' : 'ghost'} block onClick={() => setStep(3)}>
                {inviteUrl ? 'Continuar' : 'Convidar depois'}
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="form">
              <div className="option-grid" role="group" aria-label="Rotinas sugeridas">
                {routines.map((r) => (
                  <button
                    key={r.key}
                    type="button"
                    role="checkbox"
                    aria-checked={chosen.has(r.key)}
                    className="option-card"
                    onClick={() => toggle(r.key)}
                  >
                    <strong>{r.title}</strong>
                    <span>{routineWhen(r)}</span>
                  </button>
                ))}
              </div>
              <Button variant="primary" block loading={busy} onClick={() => finish(true)}>
                {chosen.size ? `Adicionar ${chosen.size} rotina(s) e começar` : 'Começar sem rotinas'}
              </Button>
              {chosen.size > 0 && (
                <Button variant="ghost" block disabled={busy} onClick={() => finish(false)}>
                  Pular esta etapa
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
